import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  IconButton,
  Icons,
  Tooltip,
  Stack,
  tokens,
} from '@cast/design-system';
import type { ChatMessage } from '@cast/types';

import { notReachable } from '@cast/utils';

import { CopyResponseButton } from './_components/CopyResponseButton';
import { BugReportForm } from './BugReportForm';
import type { Feedback } from './feedbackAnalytics';
import { NegativeFeedbackForm } from './NegativeFeedbackForm';

type FeedbackVariant = 'like' | 'dislike' | 'bug';

export const useResponseFeedback = () => {
  const [messagesFeedbacks, setMessagesFeedbacks] = useState<
    Record<ChatMessage['id'], Feedback>
  >({});

  const saveMessageFeedback = useCallback(
    ({ messageId, feedback }: { messageId: string; feedback: Feedback }) => {
      setMessagesFeedbacks((prev) => ({
        ...prev,
        [messageId]: feedback,
      }));
    },
    []
  );

  const clearMessageFeedback = useCallback(
    ({ messageId }: { messageId: string }) => {
      setMessagesFeedbacks((prev) => {
        const newMessagesFeedbacks = { ...prev };
        delete newMessagesFeedbacks[messageId];
        return newMessagesFeedbacks;
      });
    },
    []
  );

  const getMessageFeedback = useCallback(
    (messageId: string): Feedback | undefined => {
      return messagesFeedbacks[messageId];
    },
    [messagesFeedbacks]
  );

  return {
    getMessageFeedback,
    saveMessageFeedback,
    clearMessageFeedback,
  };
};

type OpenForm = 'dislike' | 'bug' | null;

const FEEDBACK_BUTTONS: {
  variant: FeedbackVariant;
  icon: typeof Icons.ThumbsUp;
  tooltip: string;
}[] = [
  {
    variant: 'like',
    icon: Icons.ThumbsUp,
    tooltip: 'Give positive feedback',
  },
  {
    variant: 'dislike',
    icon: Icons.ThumbsDown,
    tooltip: 'Give negative feedback',
  },
  {
    variant: 'bug',
    icon: Icons.BugBeetle,
    tooltip: 'Report a bug / technical issue',
  },
];

const FeedbackButton = ({
  icon: Icon,
  tooltip,
  isSelected,
  onClick,
}: {
  icon: typeof Icons.ThumbsUp;
  tooltip: string;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <Tooltip title={tooltip} placement="top">
    <span>
      <IconButton
        variant="ghost"
        size="sm"
        onClick={onClick}
        aria-label={tooltip}
        sx={{
          backgroundColor: isSelected
            ? tokens.theme.bg.structural.surfaceRaised
            : 'transparent',
        }}
      >
        <Icon
          weight={isSelected ? 'fill' : 'bold'}
          color={tokens.theme.fg.action.onGhost}
        />
      </IconButton>
    </span>
  </Tooltip>
);

const scrollFeedbackIntoView = ({
  isLatest,
  onScrollToBottom,
  container,
}: {
  isLatest: boolean;
  onScrollToBottom?: () => void;
  container: HTMLElement | null;
}) => {
  requestAnimationFrame(() => {
    if (isLatest && onScrollToBottom) {
      onScrollToBottom();
      return;
    }

    container?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
};

type Props = {
  isLatest: boolean;
  savedFeedback?: Feedback;
  onFeedback: (feedback: Feedback) => void;
  onClearFeedback: (feedback: Feedback) => void;
  onScrollToBottom?: () => void;
  messageText: string;
};

export const ResponseFeedback = ({
  isLatest,
  savedFeedback,
  onFeedback,
  onClearFeedback,
  onScrollToBottom,
  messageText,
}: Props) => {
  const [openForm, setOpenForm] = useState<OpenForm>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isFormOpen = openForm !== null;

  const handleOpenForm = useCallback(
    (form: Exclude<OpenForm, null>) => {
      setOpenForm(form);
      scrollFeedbackIntoView({
        isLatest,
        onScrollToBottom,
        container: containerRef.current,
      });
    },
    [isLatest, onScrollToBottom]
  );

  useEffect(() => {
    if (!savedFeedback || isFormOpen) return;

    scrollFeedbackIntoView({
      isLatest,
      onScrollToBottom,
      container: containerRef.current,
    });
  }, [isLatest, savedFeedback, isFormOpen, onScrollToBottom]);

  return (
    <Stack ref={containerRef} gap={12}>
      <Stack direction="row" gap={4} alignItems="center">
        <CopyResponseButton text={messageText} />
        {FEEDBACK_BUTTONS.map(({ variant, icon, tooltip }) => (
          <FeedbackButton
            key={variant}
            icon={icon}
            tooltip={tooltip}
            isSelected={
              (variant === savedFeedback?.variant && !isFormOpen) ||
              openForm === variant
            }
            onClick={() => {
              if (savedFeedback?.variant === variant) {
                onClearFeedback(savedFeedback);
                setOpenForm(null);
                return;
              }

              switch (variant) {
                case 'like':
                  onFeedback({ variant: 'like' });
                  setOpenForm(null);
                  break;
                case 'dislike':
                  handleOpenForm('dislike');
                  break;
                case 'bug':
                  handleOpenForm('bug');
                  break;
                default:
                  notReachable(variant);
              }
            }}
          />
        ))}
      </Stack>
      {openForm === 'dislike' && (
        <NegativeFeedbackForm
          onClose={() => setOpenForm(null)}
          onSelect={(reason, details) => {
            setOpenForm(null);
            onFeedback({ variant: 'dislike', reason, details });
          }}
        />
      )}
      {openForm === 'bug' && (
        <BugReportForm
          onClose={() => setOpenForm(null)}
          onSubmit={(details) => {
            setOpenForm(null);
            onFeedback({ variant: 'bug', details });
          }}
        />
      )}
      {!!savedFeedback && !isFormOpen && (
        <Alert noIcon color="info">
          Thanks for your feedback, it helps us improve.
        </Alert>
      )}
    </Stack>
  );
};
